<template>
  <div>
    <div v-bind="getRootProps()">
      <div class="flex flex-wrap items-center">
        <div
          v-for="(file, index) in files"
          :key="index"
          class="mr-4 mb-4 items-start relative border rounded p-2 bg-gray-50"
          @click.stop
        >
          <button
            type="button"
            class="mr-0 absolute top-[-10px] right-[-10px] z-10"
            @click.stop="$emit('removeFile', index)"
          >
            <i
              class="fa-solid fa-circle-xmark text-red-500 bg-white rounded-full text-lg"
            ></i>
          </button>
          <a
            :href="downloadUrl(file)"
            target="_blank"
            class="flex flex-col items-center"
          >
            <img
              class="max-w-[150px] max-h-[200px] mb-2"
              :src="fileUrl(file)"
            />
            <span
              class="text-xs text-blueDianne max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap"
              :title="file.name"
            >
              {{ file.name }}
            </span>
          </a>
        </div>
      </div>
      <input v-bind="getInputProps()" />
      <div v-if="uploadProgress !== null" class="w-full my-5">
        <div class="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
          <div
            class="bg-tiara-600 h-2.5 rounded-full transition-all duration-300"
            :style="{ width: uploadProgress + '%' }"
          ></div>
        </div>
        <p class="text-sm text-center mt-1 text-gray-600">
          Uploading... {{ uploadProgress }}%
        </p>
      </div>
      <p
        v-else
        class="inline-block cursor-pointer bg-tiara-600 uppercase text-white text-sm px-4 py-2 rounded my-5"
      >
        <span v-if="isDragActive">Drop the files here</span>
        <span v-else>{{ prompt || "Select a file..." }}</span>
      </p>
    </div>
  </div>
</template>

<script>
import { useDropzone } from "vue3-dropzone";
import { getCurrentInstance, ref } from "vue";
import { utils } from "common-frontend";

const { keysToCamel, keysToSnake } = utils;

export default {
  name: "Dropzone",
  props: {
    files: Array,
    maxFiles: Number,
    accept: [String, Array],
    prompt: String,
  },
  setup(props, context) {
    const app = getCurrentInstance();
    const { $api } = app.appContext.config.globalProperties;
    const uploadProgress = ref(null);

    const saveFiles = async (files) => {
      const totalSize = Array.from(files).reduce((acc, f) => acc + f.size, 0);
      const loadedPerFile = new Map();
      uploadProgress.value = 0;

      const uploadPromises = Array.from(files).map(async (file) => {
        try {
          // 1. Get Signed URL
          const signedUrlResponse = await $api.axios.post(
            `${import.meta.env.VITE_API_BASE_URL}/file-upload/signed-url/`,
            keysToSnake({
              filename: file.name,
              contentType: file.type || "application/octet-stream",
            }),
          );
          const { url, fileId, contentType } = keysToCamel(
            signedUrlResponse.data,
          );

          // 2. Upload to GCS
          await $api.axiosPublic.put(url, file, {
            headers: {
              "Content-Type": contentType,
            },
            onUploadProgress: (progressEvent) => {
              loadedPerFile.set(file.name, progressEvent.loaded);
              const currentTotalLoaded = Array.from(
                loadedPerFile.values(),
              ).reduce((a, b) => a + b, 0);
              uploadProgress.value = Math.round(
                (currentTotalLoaded / totalSize) * 100,
              );
            },
          });

          // 3. Notify Backend
          const completeResponse = await $api.axios.post(
            `${import.meta.env.VITE_API_BASE_URL}/file-upload/complete/`,
            keysToSnake({
              fileId,
            }),
          );

          if (
            completeResponse.data.files &&
            completeResponse.data.files.length > 0
          ) {
            return keysToCamel(completeResponse.data.files[0]);
          }
        } catch (err) {
          console.error("Upload failed for file", file.name, err); // eslint-disable-line
        }
        return null;
      });

      const results = await Promise.all(uploadPromises);
      uploadProgress.value = null;
      const uploadedFiles = results.filter((f) => f !== null);

      if (uploadedFiles.length > 0) {
        context.emit("uploaded", { files: uploadedFiles });
      }
    };

    function onDrop(acceptFiles) {
      saveFiles(acceptFiles);
    }

    const opts = {
      maxFiles: props.maxFiles,
      onDrop,
      accept: props.accept,
    };

    const { getRootProps, getInputProps, ...rest } = useDropzone(opts);

    return {
      getRootProps,
      getInputProps,
      uploadProgress,
      ...rest,
    };
  },
  methods: {
    fileUrl(file) {
      if (!file) {
        return "";
      }
      const type = file.type || file.contentType || "";
      const name = (file.name || "").toLowerCase();
      const isDocument =
        type === "application/pdf" ||
        type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        type === "application/msword" ||
        type ===
          "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
        type === "application/vnd.ms-powerpoint" ||
        name.endsWith(".pdf") ||
        name.endsWith(".docx") ||
        name.endsWith(".doc") ||
        name.endsWith(".pptx") ||
        name.endsWith(".ppt");

      if (isDocument) {
        return `${import.meta.env.VITE_API_BASE_URL}/file/${
          file.id || file.hashedId
        }/content/pdf-preview/`;
      }
      return `${import.meta.env.VITE_API_BASE_URL}/file/${
        file.id || file.hashedId
      }/content/`;
    },
    downloadUrl(file) {
      return `${import.meta.env.VITE_API_BASE_URL}/file/${
        file.id || file.hashedId
      }/content/`;
    },
  },
};
</script>
